"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.fillMissingTitles = void 0;
// (C) 2007-2022 GoodData Corporation
var sdk_model_1 = require("@gooddata/sdk-model");
var util_1 = require("@gooddata/util");
var DerivedMeasureTitleSuffixFactory_1 = require("./DerivedMeasureTitleSuffixFactory");
var ArithmeticMeasureTitleFactory_1 = require("./ArithmeticMeasureTitleFactory");
var OverTimeComparison_1 = require("../interfaces/OverTimeComparison");
var DEFAULT_MAX_ARITHMETIC_MEASURE_TITLE_LENGTH = 50;
function findOverTimeComparisonType(measure) {
    if (sdk_model_1.isPoPMeasure(measure)) {
        return OverTimeComparison_1.OverTimeComparisonTypes.SAME_PERIOD_PREVIOUS_YEAR;
    }
    else if (sdk_model_1.isPreviousPeriodMeasure(measure)) {
        return OverTimeComparison_1.OverTimeComparisonTypes.PREVIOUS_PERIOD;
    }
    return OverTimeComparison_1.OverTimeComparisonTypes.NOTHING;
}
function containsMeasureTitleItem(measureTitleProps, localIdentifier) {
    return measureTitleProps.some(function (prop) { return prop.localIdentifier === localIdentifier; });
}
function containsMeasureTitleItems(measureTitleProps, localIdentifiers) {
    return localIdentifiers.every(function (identifier) { return containsMeasureTitleItem(measureTitleProps, identifier); });
}
function findMeasureTitleItem(measureTitles, localIdentifier) {
    return measureTitles.find(function (prop) { return prop.localIdentifier === localIdentifier; }) || null;
}
function findTitleForDerivedMeasure(measure, measureTitleProps, suffixFactory) {
    var masterMeasureIdentifier = sdk_model_1.measureMasterIdentifier(measure);
    if (!masterMeasureIdentifier) {
        return undefined;
    }
    var measureProps = findMeasureTitleItem(measureTitleProps, masterMeasureIdentifier);
    if (measureProps === null) {
        return undefined;
    }
    var derivedMeasureTitleBase = measureProps.alias || measureProps.title || "";
    var overTimeComparisonType = findOverTimeComparisonType(measure);
    return derivedMeasureTitleBase + suffixFactory.getSuffix(overTimeComparisonType);
}
function buildMeasureTitle(measure) {
    if (sdk_model_1.isSimpleMeasure(measure)) {
        var alias = sdk_model_1.measureAlias(measure);
        var localIdentifier = sdk_model_1.measureLocalId(measure);
        var title = sdk_model_1.measureTitle(measure);
        return {
            localIdentifier: localIdentifier,
            title: title,
            alias: alias,
        };
    }
    return null;
}
function buildArithmeticMeasureTitle(measure, measureTitleProps, titleFactory, maxArithmeticMeasureTitleLength) {
    if (sdk_model_1.isArithmeticMeasure(measure)) {
        var alias = sdk_model_1.measureAlias(measure);
        var localIdentifier = sdk_model_1.measureLocalId(measure);
        var measureIdentifiers = sdk_model_1.measureArithmeticOperands(measure);
        var operator = sdk_model_1.measureArithmeticOperator(measure);
        if (containsMeasureTitleItems(measureTitleProps, measureIdentifiers)) {
            var fullLengthTitle = titleFactory.getTitle({
                operator: operator,
                masterMeasureLocalIdentifiers: measureIdentifiers,
            }, measureTitleProps);
            if (!fullLengthTitle) {
                return null;
            }
            var title = util_1.stringUtils.shortenText(fullLengthTitle, {
                maxLength: maxArithmeticMeasureTitleLength,
            });
            return {
                localIdentifier: localIdentifier,
                title: title,
                alias: alias,
            };
        }
    }
    return null;
}
function buildDerivedMeasureTitle(measure, measureTitleProps, suffixFactory) {
    if (sdk_model_1.isPoPMeasure(measure) || sdk_model_1.isPreviousPeriodMeasure(measure)) {
        var alias = sdk_model_1.measureAlias(measure);
        var localIdentifier = sdk_model_1.measureLocalId(measure);
        var masterMeasureIdentifier = sdk_model_1.measureMasterIdentifier(measure);
        if (containsMeasureTitleItem(measureTitleProps, masterMeasureIdentifier)) {
            return {
                localIdentifier: localIdentifier,
                title: findTitleForDerivedMeasure(measure, measureTitleProps, suffixFactory),
                alias: alias,
            };
        }
    }
    return null;
}
function buildMeasureTitles(measures, locale, maxArithmeticMeasureTitleLength) {
    var titleFactory = new ArithmeticMeasureTitleFactory_1.ArithmeticMeasureTitleFactory(locale);
    var suffixFactory = new DerivedMeasureTitleSuffixFactory_1.DerivedMeasureTitleSuffixFactory(locale);
    var measureTitleProps = [];
    var isMeasureTitlePropsChanged = true;
    while (isMeasureTitlePropsChanged) {
        isMeasureTitlePropsChanged = false;
        measures.forEach(function (measure) {
            var localId = sdk_model_1.measureLocalId(measure);
            if (!containsMeasureTitleItem(measureTitleProps, localId)) {
                var newMeasureTitleProp = buildMeasureTitle(measure) ||
                    buildArithmeticMeasureTitle(measure, measureTitleProps, titleFactory, maxArithmeticMeasureTitleLength) ||
                    buildDerivedMeasureTitle(measure, measureTitleProps, suffixFactory);
                if (newMeasureTitleProp !== null) {
                    measureTitleProps.push(newMeasureTitleProp);
                    isMeasureTitlePropsChanged = true;
                }
            }
        });
    }
    return measureTitleProps;
}
function updateBucketItemTitle(bucketItem, measureTitleProps) {
    if (sdk_model_1.isMeasure(bucketItem)) {
        var localId = sdk_model_1.measureLocalId(bucketItem);
        var measureTitleProp = findMeasureTitleItem(measureTitleProps, localId);
        if (measureTitleProp !== null) {
            var title_1 = measureTitleProp.title, alias_1 = measureTitleProp.alias;
            return sdk_model_1.modifyMeasure(bucketItem, function (measure) { return measure.title(title_1).alias(alias_1); });
        }
    }
    return bucketItem;
}
function updateVisualizationObjectTitles(insight, measureTitleProps) {
    return sdk_model_1.insightModifyItems(insight, function (item) { return updateBucketItemTitle(item, measureTitleProps); });
}
/**
 * The function fills the titles of the measures that does not have it set.
 *
 * The derived measures
 * have the title built from the current name of the master measure and suffix based on the derived measure type.
 *
 * The arithmetic measures
 * have the title built from the current names of the referenced master measures and type of the arithmetic
 * operation.
 *
 * @param insight - insight or insight definition that must be processed.
 * @param locale - locale used for localization of the measure titles.
 * @param maxArithmeticMeasureTitleLength - maximum length of generated arithmetic measures titles.
 * Longer names will be shortened. Default value is 50 characters.
 *
 * @returns a copy of insight with auto-generated titles for derived and arithmetic measures
 *
 * @internal
 */
function fillMissingTitles(insight, locale, maxArithmeticMeasureTitleLength) {
    if (maxArithmeticMeasureTitleLength === void 0) { maxArithmeticMeasureTitleLength = DEFAULT_MAX_ARITHMETIC_MEASURE_TITLE_LENGTH; }
    var measures = sdk_model_1.insightMeasures(insight);
    var measureTitleProps = buildMeasureTitles(measures, locale, maxArithmeticMeasureTitleLength);
    return updateVisualizationObjectTitles(insight, measureTitleProps);
}
exports.fillMissingTitles = fillMissingTitles;
//# sourceMappingURL=fillMissingTitles.js.map